import { useRef, useState } from 'react';
import { FileText, ImageIcon, Loader2, Paperclip, X } from 'lucide-react';
import { HELP_FILE_MAX_BYTES, HELP_MAX_FILES, helpFileType } from '@ready/shared';
import { useApi, type HelpFile } from '@/lib/api';
import { put } from '@/components/course/upload';

/*
 * Screenshots and PDFs for help requests: picked (or taken, on phones),
 * uploaded straight to storage, then scanned. The form sends the ids of the
 * finished ones.
 */

export interface PendingFile {
  key: string;
  filename: string;
  id: string | null;
  phase: 'uploading' | 'done' | 'error';
  error?: string;
}

export function useAttachments() {
  const api = useApi();
  const [files, setFiles] = useState<PendingFile[]>([]);
  const update = (key: string, patch: Partial<PendingFile>) =>
    setFiles((list) => list.map((f) => (f.key === key ? { ...f, ...patch } : f)));

  const add = async (picked: FileList | null) => {
    if (!picked) return;
    const room = HELP_MAX_FILES - files.length;
    for (const file of [...picked].slice(0, Math.max(room, 0))) {
      const key = `${file.name}-${file.size}-${Math.random()}`;
      setFiles((list) => [...list, { key, filename: file.name, id: null, phase: 'uploading' }]);
      if (!helpFileType(file.name)) {
        update(key, { phase: 'error', error: 'Use a PNG, JPEG, WebP or PDF file' });
        continue;
      }
      if (file.size > HELP_FILE_MAX_BYTES) {
        update(key, { phase: 'error', error: 'Files can be up to 5 MB' });
        continue;
      }
      try {
        const { file: row, upload } = await api.requestHelpUpload({
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
        });
        await put(upload.url, upload.headers, file, () => {});
        await api.completeHelpUpload(row.id);
        update(key, { id: row.id, phase: 'done' });
      } catch (err) {
        update(key, { phase: 'error', error: err instanceof Error ? err.message : String(err) });
      }
    }
  };
  return {
    files,
    add,
    remove: (key: string) => setFiles((list) => list.filter((f) => f.key !== key)),
    clear: () => setFiles([]),
    ids: files.filter((f) => f.phase === 'done' && f.id).map((f) => f.id!),
    busy: files.some((f) => f.phase === 'uploading'),
  };
}

export function AttachmentPicker({ state }: { state: ReturnType<typeof useAttachments> }) {
  const input = useRef<HTMLInputElement>(null);
  const full = state.files.length >= HELP_MAX_FILES;
  return (
    <div className="space-y-2">
      <input
        ref={input}
        type="file"
        multiple
        accept="image/png,image/jpeg,image/webp,application/pdf"
        className="sr-only"
        aria-label="Attach screenshots or PDFs"
        onChange={(e) => {
          void state.add(e.target.files);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        disabled={full}
        onClick={() => input.current?.click()}
        className="-my-1 inline-flex min-h-10 items-center gap-2 py-1 text-sm text-primary hover:underline disabled:text-muted-foreground disabled:no-underline"
      >
        <Paperclip className="size-4" aria-hidden />
        {full ? `Up to ${HELP_MAX_FILES} files` : 'Attach a screenshot or PDF'}
      </button>
      {state.files.length > 0 && (
        <ul className="space-y-1">
          {state.files.map((f) => (
            <li
              key={f.key}
              className="flex items-center gap-2 rounded-md bg-muted/50 px-2 py-1.5 text-sm"
            >
              {f.phase === 'uploading' ? (
                <Loader2
                  className="size-4 shrink-0 animate-spin text-muted-foreground"
                  aria-hidden
                />
              ) : f.filename.toLowerCase().endsWith('.pdf') ? (
                <FileText className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              ) : (
                <ImageIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              )}
              <span className="min-w-0 flex-1 truncate">{f.filename}</span>
              {f.phase === 'error' && <span className="text-xs text-destructive">{f.error}</span>}
              <button
                type="button"
                aria-label={`Remove ${f.filename}`}
                onClick={() => state.remove(f.key)}
                className="flex size-7 items-center justify-center rounded text-muted-foreground hover:bg-muted"
              >
                <X className="size-4" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Attachments in a message: image thumbnails and PDF links (only once scanned clean). */
export function AttachmentList({ files }: { files: HelpFile[] }) {
  if (!files.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {files.map((f) =>
        !f.url ? (
          <span key={f.id} className="rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">
            {f.filename} · {f.status === 'scanning' ? 'checking…' : 'unavailable'}
          </span>
        ) : f.contentType.startsWith('image/') ? (
          <a key={f.id} href={f.url} target="_blank" rel="noreferrer" title={f.filename}>
            <img
              src={f.url}
              alt={f.filename}
              className="h-24 w-auto max-w-[12rem] rounded-md border border-border object-cover"
            />
          </a>
        ) : (
          <a
            key={f.id}
            href={f.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
          >
            <FileText className="size-4" aria-hidden /> {f.filename}
          </a>
        ),
      )}
    </div>
  );
}
